import { observer } from "mobx-react-lite";
import { DatabaseIcon, Info, SearchIcon } from "lucide-react";
import { useCallback, useState } from "react";

import ValueInput from "./ValueInput";
import ValueSourceSelector from "./ValueSourceSelector";

import { ContainsConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { conditional, custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import Checkbox from "@/components/ui/checkbox";
import { BasicTooltip } from "@/components/ui/Tooltip";

const validationSchema = createBlockSchema<ContainsConfiguration>()
  .requireName()
  .addRule(required("configuration.source", "Source is required"))
  .addRule(
    conditional(
      "configuration.value_source",
      payload => payload.configuration?.value_source !== undefined,
      required("configuration.value_source", "Value source is required"),
    ),
  )
  .addRule(
    custom(
      "configuration",
      (_, payload) => {
        const config = payload.configuration;
        const hasValue = config?.value && config.value.trim() !== "";
        const hasValueSource = config?.value_source && config.value_source.trim() !== "";
        return Boolean(hasValue || hasValueSource);
      },
      "Please provide either a search value or select a source variable",
      ["configuration.value", "configuration.value_source"],
    ),
  )
  .build();

const ContainsBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<ContainsConfiguration>, "DefaultFields">) => {
    const block = useBlock<ContainsConfiguration>();
    const isReadonly = useIsReadonly();
    const [useValueSource, setUseValueSource] = useState<boolean>(
      !!block.configuration?.value_source,
    );

    const handleValueChange = useCallback(
      (value: string) => {
        block.setConfiguration({
          ...block.configuration,
          value,
          source: block.configuration?.source || "",
          value_source: block.configuration?.value_source,
        });
      },
      [block],
    );

    const handleValueSourceChange = useCallback(
      (source: string) => {
        block.setConfiguration({
          ...block.configuration,
          value_source: source,
          source: block.configuration?.source || "",
          value: undefined, // Clear value when using source
        });
      },
      [block],
    );

    const handleUseValueSourceToggle = useCallback(
      (useSource: boolean) => {
        setUseValueSource(useSource);
        if (useSource) {
          // Switch to source mode - clear value and set value_source to empty string
          const newConfig = {
            ...block.configuration,
            value: undefined,
            value_source: "", // Empty string indicates source mode but no source selected yet
            source: block.configuration?.source || "",
          };
          block.setConfiguration(newConfig);
        } else {
          // Switch to direct value mode - clear value_source
          const newConfig = {
            ...block.configuration,
            value: "",
            value_source: undefined,
            source: block.configuration?.source || "",
          };
          block.setConfiguration(newConfig);
        }
      },
      [block],
    );

    return (
      <>
        <SectionGroup
          title="Search Configuration"
          description="Configure the value to search for"
          icon={<SearchIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            {useValueSource ? (
              <ValidatedField name="Value Source" path="configuration.value_source">
                <ValueSourceSelector
                  valueSource={block.configuration?.value_source || null}
                  onValueSourceChange={handleValueSourceChange}
                  availableSources={Object.values(sources).flat()}
                  disabled={isReadonly}
                />
              </ValidatedField>
            ) : (
              <ValidatedField name="Search Value" path="configuration.value">
                <ValueInput
                  value={block.configuration?.value || null}
                  onValueChange={handleValueChange}
                  disabled={isReadonly}
                />
              </ValidatedField>
            )}

            <ValidatedField name="Use Source Variable for Search Value" path="">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="use-value-source"
                  checked={useValueSource}
                  disabled={isReadonly}
                  onChange={e => handleUseValueSourceToggle(e.target.checked)}
                  label="Use source variable for search value"
                />
                <BasicTooltip content="You can use a source variable for the search value. This will use the value of the source as the search term.">
                  <Info className="h-4 w-4 cursor-pointer hover:text-gray-500" />
                </BasicTooltip>
              </div>
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to search within"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<ContainsConfiguration>(ContainsBlockCore, {
  validationSchema,
});
